import { inject, Injectable, NgZone, signal } from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { LogService } from 'shared';

export type LockTimeout = 'immediate' | '30s' | '1m' | '5m';

const LOCK_TIMEOUT_KEY = 'lock_timeout';
const TIMEOUT_VALUES: Record<LockTimeout, number> = {
  immediate: 0,
  '30s': 30_000,
  '1m': 60_000,
  '5m': 300_000,
};

@Injectable({
  providedIn: 'root',
})
export class AppLockService {
  private readonly zone = inject(NgZone);
  private readonly logger = inject(LogService);

  private backgroundTimestamp: number | null = null;
  private lockTimeoutMs = 0;
  private listenerActive = false;

  // In-memory signals — default to locked on cold start
  private readonly _isLocked = signal(true);
  private readonly _showBlur = signal(false);

  readonly isLocked = this._isLocked.asReadonly();
  readonly showBlur = this._showBlur.asReadonly();

  async initialize(pinConfigured: boolean): Promise<void> {
    // Only lock if PIN is configured
    this._isLocked.set(pinConfigured);
    this._showBlur.set(false);

    await this.loadTimeout();
    this.startListening();
  }

  // ── Lock State ──

  unlock(): void {
    this._isLocked.set(false);
    this._showBlur.set(false);
    this.backgroundTimestamp = null;
  }

  lock(): void {
    this._isLocked.set(true);
  }

  setLocked(locked: boolean): void {
    this._isLocked.set(locked);
    if (!locked) this._showBlur.set(false);
  }

  // ── Timeout Configuration ──

  async getTimeout(): Promise<LockTimeout> {
    const { value } = await Preferences.get({ key: LOCK_TIMEOUT_KEY });
    return (value as LockTimeout) || 'immediate';
  }

  async setTimeout(timeout: LockTimeout): Promise<void> {
    this.lockTimeoutMs = TIMEOUT_VALUES[timeout];
    await Preferences.set({ key: LOCK_TIMEOUT_KEY, value: timeout });
  }

  // ── App State Listener ──

  private startListening(): void {
    if (this.listenerActive || !Capacitor.isNativePlatform()) return;
    this.listenerActive = true;

    void App.addListener('appStateChange', (state) => {
      this.zone.run(() => {
        if (!state.isActive) {
          this.onBackground();
        } else {
          this.onForeground();
        }
      });
    });
  }

  private onBackground(): void {
    this.backgroundTimestamp = Date.now();
    this._showBlur.set(true);
  }

  private onForeground(): void {
    this._showBlur.set(false);

    if (this._isLocked()) return; // Already locked, nothing to do

    if (this.backgroundTimestamp === null) return;

    const elapsed = Date.now() - this.backgroundTimestamp;
    this.backgroundTimestamp = null;

    if (elapsed >= this.lockTimeoutMs) {
      this.logger.info(`Lock triggered after ${elapsed}ms background`, 'AppLockService');
      this._isLocked.set(true);
    }
  }

  private async loadTimeout(): Promise<void> {
    const timeout = await this.getTimeout();
    this.lockTimeoutMs = TIMEOUT_VALUES[timeout];
  }
}
